import { CdkTable } from '@angular/cdk/table';
import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   OnDestroy,
   Output,
   ViewChild,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NullActionTypes } from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AutoAttendantKeyItemView } from 'src/app/common-components/auto-attendant-key-list/models/auto-attendant-key-item.model';
import { ExtensionDetailView } from 'src/app/pages/extension-details/models/extension-detail-view.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ndt-add-account-auto-attendant-keys-view',
    styleUrls: ['./add-account-auto-attendant-keys-view.component.scss'],
    templateUrl: './add-account-auto-attendant-keys-view.component.html',
    standalone: false
})
export class AddAccountAutoAttendantKeysViewComponent implements OnDestroy {
   @ViewChild('table') table: CdkTable<UntypedFormGroup>;

   @Output() formCanceled = new EventEmitter<void>();

   @Output() formSubmitted = new EventEmitter<void>();

   @Input() form: UntypedFormGroup;

   @Input() autoAttendantKeysArray: UntypedFormArray;

   @Input() addedExtensions;

   destroyed$ = new Subject<void>();

   columns: string[] = ['keyNumber', 'routeToExtension', 'routeToNullAction', 'deleteKey'];

   private autoAttendantKeyItems: AutoAttendantKeyItemView[] = [];

   constructor(private fb: UntypedFormBuilder, private changeDetectorRef: ChangeDetectorRef) {}

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onCloseClick(): void {
      this.formCanceled.emit();
   }

   onAddKey(): void {
      const newKey: AutoAttendantKeyItemView = {
         autoAttendantKeyId: 0,
         extensionAutoAttendantId: 0,
         isDeleted: false,
         keyNumber: null,
         routeToExtension: null,
         voiceMailOnly: false,
         routeToNullAction: NullActionTypes.Unset,
         selectedOption: null,
      };
      this.autoAttendantKeyItems.push(newKey);
      this.autoAttendantKeysArray.insert(this.autoAttendantKeysArray.length, this.createKeyFormGroup(newKey));

      if (this.table) {
         this.table.renderRows();
      }
   }

   onCreate(): void {
      if (this.form.valid) {
         this.formSubmitted.emit();
      }
   }

   extensionDisplayWith(option: ExtensionDetailView | string) {
      if (option === 'Name Directory') {
         return 'Name Directory';
      } else if (option === 'Hangup') {
         return 'Hangup';
      } else if (typeof option === 'object' && option !== null) {
         return `${option.number} - ${option.name}`;
      } else {
         return '';
      }
   }

   onDelete(key: AutoAttendantKeyItemView, rowIndex: number) {
      if (this.autoAttendantKeysArray.length > 0) {
         this.autoAttendantKeyItems = this.autoAttendantKeysArray.value;
         this.autoAttendantKeyItems[rowIndex].isDeleted = true;

         this.autoAttendantKeysArray.at(rowIndex).get('keyNumber')?.clearValidators();
         this.autoAttendantKeysArray.at(rowIndex).get('routeToExtension')?.clearValidators();
         this.autoAttendantKeysArray.patchValue(this.autoAttendantKeyItems);
         this.autoAttendantKeysArray.updateValueAndValidity();
      }
   }

   private createKeyFormGroup(key?: AutoAttendantKeyItemView): UntypedFormGroup {
      const formGroup = this.fb.group({
         autoAttendantKeyId: key?.autoAttendantKeyId,
         isDeleted: key?.isDeleted ?? false,
         keyNumber: this.fb.control(key?.keyNumber, [
            Validators.required,
            Validators.pattern(/^(?:\*?[0-9]|[1-9][0-9]?|#)$/),
         ]),
         routeToExtension: this.fb.control(key?.routeToExtension),
         routeToNullAction: this.fb.control(key?.routeToNullAction),
         selectedOption: this.fb.control(key?.selectedOption),
      });

      formGroup
         .get('routeToExtension')
         ?.valueChanges.pipe(takeUntil(this.destroyed$))
         .subscribe((value) => {
            if (value == 'Name Directory') {
               formGroup.get('routeToNullAction')?.patchValue(NullActionTypes.Directory);
            } else if (value == 'Hangup') {
               formGroup.get('routeToNullAction')?.patchValue(NullActionTypes.Hangup);
            } else {
               formGroup.get('routeToNullAction')?.patchValue(NullActionTypes.Unset);
            }
         });

      return formGroup;
   }
}
